import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { CircleOff } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useDiseaseHistory } from "../../DiseaseHistoryContext";
import type { BreastConditionDetail } from "../../DiseaseHistoryContext";
import type { DiseaseHistoryStackParamList } from "../../DiseaseHistoryNavigator";
import { useFlowProgress } from "../../useFlowProgress";
import { BREAST_CONDITION_OPTIONS } from "../options";
import type { BreastConditionType } from "../options";

export function BreastConditionsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useDiseaseHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<DiseaseHistoryStackParamList>>();
  const progress = useFlowProgress("BreastConditions");

  const selectedConditions = useMemo(
    () => new Set(state.breastConditions.map((c) => c.condition)),
    [state.breastConditions],
  );

  const isNone = state.hasBreastConditions === false;

  const toggleCondition = (condition: BreastConditionType) => {
    if (selectedConditions.has(condition)) {
      // Remove
      const toRemove = state.breastConditions.find(
        (c) => c.condition === condition,
      );
      const idsToDelete = toRemove?.id
        ? [...state.breastConditionIdsToDelete, toRemove.id]
        : state.breastConditionIdsToDelete;
      const remaining = state.breastConditions.filter(
        (c) => c.condition !== condition,
      );
      updateState({
        hasBreastConditions: remaining.length > 0 ? true : null,
        breastConditions: remaining,
        breastConditionIdsToDelete: idsToDelete,
      });
    } else {
      // Add
      const newCondition: BreastConditionDetail = { condition };
      updateState({
        hasBreastConditions: true,
        breastConditions: [...state.breastConditions, newCondition],
      });
    }
  };

  const handleNone = () => {
    const idsToDelete = state.breastConditions
      .filter((c) => c.id)
      .map((c) => c.id!);
    updateState({
      hasBreastConditions: false,
      breastConditions: [],
      breastConditionIdsToDelete: [
        ...state.breastConditionIdsToDelete,
        ...idsToDelete,
      ],
    });
  };

  const handleSave = async () => {
    await save();
    const currentIdx = state.navigationStack.indexOf("BreastConditions");
    const nextRoute = state.navigationStack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  const isValid =
    isNone ||
    (state.hasBreastConditions === true && state.breastConditions.length > 0);

  return (
    <QuestionLayout
      title={t("med_history.medical.disease_history.breast_conditions.title")}
      subtitle={t(
        "med_history.medical.disease_history.breast_conditions.subtitle",
      )}
      onBack={() => navigation.goBack()}
      progress={progress}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        <View style={styles.grid}>
          {BREAST_CONDITION_OPTIONS.map((option) => (
            <View key={option.id} style={styles.cell}>
              <SquareOptionButton
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={!isNone && selectedConditions.has(option.id)}
                onPress={() => {
                  if (isNone) {
                    updateState({
                      hasBreastConditions: true,
                      breastConditions: [{ condition: option.id }],
                    });
                  } else {
                    toggleCondition(option.id);
                  }
                }}
                labelSize="sm"
              />
            </View>
          ))}
          <View style={styles.cell}>
            <SquareOptionButton
              icon={CircleOff}
              label={t("med_history.medical.shared.none")}
              isSelected={isNone}
              onPress={handleNone}
              labelSize="sm"
            />
          </View>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
